// ---------------------------------------------------------------------------
// Test Mode — the simulated team's leave (Concedii).
//
// Held in a mutable module-level list, like the reminders dataset: the demo
// files, edits, approves and withdraws requests, and a demo where "Aprobă"
// does nothing reads as broken. The list resets on reload, which is what a
// demo wants.
//
// Dates are generated relative to today so the demo always has a leave in
// progress, one coming up and one already decided, whenever it is opened.
// ---------------------------------------------------------------------------

import type {
  LeaveBalance,
  LeaveDay,
  LeaveReportRow,
  LeaveRequest,
  LeaveStatus,
  LeaveType,
  PublicHoliday,
} from '../lib/leaveApi';
import { PEOPLE_REFS } from './people';
import { DEMO_USER_REF } from './tasks';

/**
 * Test Mode signs in as `demo-user`, NOT as one of the seeded people, so the
 * demo's own requests have to be keyed on that id — otherwise "Cererile mele"
 * comes back empty while the balance card still counts the days.
 */
const ME = DEMO_USER_REF;

/** Seed index 0 is the signed-in demo user; the rest are colleagues. */
const CAST = [DEMO_USER_REF, ...PEOPLE_REFS.filter((p) => p.id !== DEMO_USER_REF.id)];

function dayKey(offsetDays: number): string {
  const d = new Date();
  d.setUTCHours(12, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function isWeekend(day: string): boolean {
  const [y, m, d] = day.split('-').map(Number);
  const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return js === 0 || js === 6;
}

function eachDay(from: string, to: string): string[] {
  const days: string[] = [];
  const [y, m, d] = from.split('-').map(Number);
  for (let t = Date.UTC(y, m - 1, d); ; t += 86_400_000) {
    const key = new Date(t).toISOString().slice(0, 10);
    if (key > to) break;
    days.push(key);
  }
  return days;
}

/** Working days, ignoring public holidays — close enough for a demo. */
function workingDays(from: string, to: string): number {
  return eachDay(from, to).filter((day) => !isWeekend(day)).length;
}

function person(index: number): { id: string; name: string; email: string } {
  const ref = CAST[index % CAST.length];
  return {
    id: ref.id,
    name: ref.name,
    email: `${ref.name.toLowerCase().replace(/[^a-z]+/g, '.')}@cleancodeculture.ro`,
  };
}

interface Seed {
  id: string;
  personIndex: number;
  type: LeaveType;
  status: LeaveStatus;
  from: number;
  to: number;
  reason?: string;
  hours?: number;
  decidedByIndex?: number;
  decisionNote?: string;
}

const SEEDS: Seed[] = [
  // Someone away right now — this is what paints a band on today's calendar.
  {
    id: 'leave-1',
    personIndex: 3,
    type: 'ANNUAL',
    status: 'APPROVED',
    from: -2,
    to: 3,
    reason: 'Vacanță plănuită din primăvară.',
    decidedByIndex: 0,
  },
  // The demo user's own approved holiday, coming up.
  {
    id: 'leave-2',
    personIndex: 0,
    type: 'ANNUAL',
    status: 'APPROVED',
    from: 21,
    to: 30,
    reason: 'Concediu de vară.',
    decidedByIndex: 1,
  },
  // Waiting for a decision — the manager's queue, and the sidebar badge.
  {
    id: 'leave-3',
    personIndex: 5,
    type: 'ANNUAL',
    status: 'PENDING',
    from: 10,
    to: 14,
    reason: 'Nuntă în familie.',
  },
  {
    id: 'leave-4',
    personIndex: 7,
    type: 'SICK',
    status: 'PENDING',
    from: -1,
    to: 2,
    reason: 'Certificat medical trimis pe email.',
  },
  // An hours-long absence: costs no leave day.
  {
    id: 'leave-5',
    personIndex: 2,
    type: 'SHORT_ABSENCE',
    status: 'APPROVED',
    from: 6,
    to: 6,
    hours: 3,
    reason: 'Programare la medic.',
    decidedByIndex: 0,
  },
  // A refusal, so the demo shows what one looks like.
  {
    id: 'leave-6',
    personIndex: 9,
    type: 'UNPAID',
    status: 'REJECTED',
    from: -20,
    to: -14,
    reason: 'Vreau să prelungesc concediul.',
    decidedByIndex: 0,
    decisionNote: 'Nu în sprintul de release; hai să reluăm în noiembrie.',
  },
  // History, for the per-person tab and the report.
  {
    id: 'leave-7',
    personIndex: 0,
    type: 'ANNUAL',
    status: 'APPROVED',
    from: -120,
    to: -114,
    decidedByIndex: 1,
  },
  {
    id: 'leave-8',
    personIndex: 4,
    type: 'SPECIAL_EVENT',
    status: 'APPROVED',
    from: -60,
    to: -58,
    reason: 'Nuntă.',
    decidedByIndex: 0,
  },
];

function build(seed: Seed): LeaveRequest {
  const startDate = dayKey(seed.from);
  const endDate = dayKey(seed.to);
  const who = person(seed.personIndex);
  const decider = seed.decidedByIndex !== undefined ? person(seed.decidedByIndex) : null;
  const mine = who.id === ME.id;
  const pending = seed.status === 'PENDING';
  return {
    id: seed.id,
    userId: who.id,
    user: who,
    type: seed.type,
    status: seed.status,
    startDate,
    endDate,
    hours: seed.hours ?? null,
    workingDays: seed.type === 'SHORT_ABSENCE' ? 0 : workingDays(startDate, endDate),
    reason: seed.reason ?? null,
    attachments: [],
    decidedBy: decider ? { id: decider.id, name: decider.name } : null,
    decidedAt: decider ? new Date(Date.now() - 86_400_000).toISOString() : null,
    decisionNote: seed.decisionNote ?? null,
    createdAt: new Date(Date.now() - 7 * 86_400_000).toISOString(),
    updatedAt: new Date(Date.now() - 86_400_000).toISOString(),
    // The demo signs in as an admin, so everything is permitted except
    // approving one's own request — the rule worth showing off.
    permissions: {
      canEdit: pending,
      canCancel: pending || seed.status === 'APPROVED',
      canDecide: pending && !mine,
      canDelete: true,
    },
  };
}

let requests: LeaveRequest[] = SEEDS.map(build);

export function leaveRequests(): LeaveRequest[] {
  return requests;
}

/** Filter the mutable list the way the API would. */
export function filterLeaveRequests(params: URLSearchParams): LeaveRequest[] {
  const userId = params.get('userId');
  const status = params.get('status')?.split(',').filter(Boolean);
  const type = params.get('type')?.split(',').filter(Boolean);
  const from = params.get('from');
  const to = params.get('to');
  return requests.filter((r) => {
    if (userId && r.userId !== userId) return false;
    if (status?.length && !status.includes(r.status)) return false;
    if (type?.length && !type.includes(r.type)) return false;
    if (from && r.endDate < from) return false;
    if (to && r.startDate > to) return false;
    return true;
  });
}

export function createLeaveRequestMock(input: Record<string, unknown>): LeaveRequest {
  const startDate = String(input.startDate ?? dayKey(1));
  const endDate = String(input.endDate ?? startDate);
  const type = (input.type as LeaveType) ?? 'ANNUAL';
  const created: LeaveRequest = {
    id: `leave-${Date.now()}`,
    userId: ME.id,
    user: { id: ME.id, name: ME.name, email: person(0).email },
    type,
    status: 'PENDING',
    startDate,
    endDate,
    hours: typeof input.hours === 'number' ? input.hours : null,
    workingDays: type === 'SHORT_ABSENCE' ? 0 : workingDays(startDate, endDate),
    reason: typeof input.reason === 'string' ? input.reason : null,
    attachments: [],
    decidedBy: null,
    decidedAt: null,
    decisionNote: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    permissions: { canEdit: true, canCancel: true, canDecide: false, canDelete: true },
  };
  requests = [created, ...requests];
  return created;
}

export function patchLeaveRequestMock(
  id: string,
  patch: Partial<LeaveRequest>,
): LeaveRequest | null {
  const found = requests.find((r) => r.id === id);
  if (!found) return null;
  const next: LeaveRequest = {
    ...found,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  if (next.type !== 'SHORT_ABSENCE') {
    next.workingDays = workingDays(next.startDate, next.endDate);
  }
  next.permissions = {
    canEdit: next.status === 'PENDING',
    canCancel: next.status === 'PENDING' || next.status === 'APPROVED',
    canDecide: next.status === 'PENDING' && next.userId !== ME.id,
    canDelete: true,
  };
  requests = requests.map((r) => (r.id === id ? next : r));
  return next;
}

export function decideLeaveRequestMock(id: string, approve: boolean, note: string | null) {
  return patchLeaveRequestMock(id, {
    status: approve ? 'APPROVED' : 'REJECTED',
    decidedBy: { id: ME.id, name: ME.name },
    decidedAt: new Date().toISOString(),
    decisionNote: note,
  });
}

export function deleteLeaveRequestMock(id: string): void {
  requests = requests.filter((r) => r.id !== id);
}

/** Approved leave in a window, flattened per covered day — as the API returns it. */
export function leaveDays(from: string, to: string, userId?: string | null): LeaveDay[] {
  const out: LeaveDay[] = [];
  for (const r of requests) {
    if (r.status !== 'APPROVED') continue;
    if (userId && r.userId !== userId) continue;
    for (const day of eachDay(r.startDate, r.endDate)) {
      if (day < from || day > to) continue;
      out.push({
        requestId: r.id,
        userId: r.userId,
        userName: r.user.name,
        type: r.type,
        day,
        startDate: r.startDate,
        endDate: r.endDate,
        hours: r.hours,
      });
    }
  }
  return out;
}

const DEFAULT_ENTITLEMENT = 21;
/** Per-person overrides the demo's entitlement editor writes into. */
const entitlements = new Map<string, number>();

export function setLeaveEntitlementMock(personId: string, days: number | null) {
  if (days === null) entitlements.delete(personId);
  else entitlements.set(personId, days);
  return { personId, annualLeaveDays: days };
}

export function leaveBalance(userId: string, year: number): LeaveBalance {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const byType = {
    ANNUAL: 0,
    SICK: 0,
    UNPAID: 0,
    SPECIAL_EVENT: 0,
    SHORT_ABSENCE: 0,
  } as Record<LeaveType, number>;
  let usedDays = 0;
  let pendingDays = 0;

  for (const r of requests) {
    if (r.userId !== userId) continue;
    if (r.endDate < yearStart || r.startDate > yearEnd) continue;
    const days = workingDays(
      r.startDate < yearStart ? yearStart : r.startDate,
      r.endDate > yearEnd ? yearEnd : r.endDate,
    );
    if (r.status === 'APPROVED') {
      byType[r.type] += days;
      if (r.type === 'ANNUAL') usedDays += days;
    } else if (r.status === 'PENDING' && r.type === 'ANNUAL') {
      pendingDays += days;
    }
  }

  const override = entitlements.get(userId);
  const entitledDays = override ?? DEFAULT_ENTITLEMENT;
  return {
    userId,
    year,
    entitledDays,
    usedDays,
    pendingDays,
    remainingDays: Math.max(0, entitledDays - usedDays - pendingDays),
    isDefaultEntitlement: override === undefined,
    byType,
  };
}

export function leaveReport(year: number): LeaveReportRow[] {
  return CAST.map((ref, i) => ({
    userId: ref.id,
    personId: ref.id,
    name: ref.name,
    email: person(i).email,
    balance: leaveBalance(ref.id, year),
  }));
}

export function pendingLeaveCount(): number {
  return requests.filter((r) => r.status === 'PENDING').length;
}

/**
 * Romanian legal holidays for the demo. A small fixed set is enough here — the
 * real calendar (with Orthodox Easter) lives on the server.
 */
export function publicHolidaysMock(year: number, through?: number): PublicHoliday[] {
  const out: PublicHoliday[] = [];
  for (let y = year; y <= (through ?? year); y += 1) {
    out.push(
      { day: `${y}-01-01`, name: 'Anul Nou' },
      { day: `${y}-01-02`, name: 'Anul Nou' },
      { day: `${y}-01-06`, name: 'Bobotează' },
      { day: `${y}-01-07`, name: 'Sfântul Ioan Botezătorul' },
      { day: `${y}-01-24`, name: 'Ziua Unirii Principatelor Române' },
      { day: `${y}-05-01`, name: 'Ziua Muncii' },
      { day: `${y}-06-01`, name: 'Ziua Copilului' },
      { day: `${y}-08-15`, name: 'Adormirea Maicii Domnului' },
      { day: `${y}-11-30`, name: 'Sfântul Andrei' },
      { day: `${y}-12-01`, name: 'Ziua Națională' },
      { day: `${y}-12-25`, name: 'Crăciunul' },
      { day: `${y}-12-26`, name: 'Crăciunul' },
    );
  }
  return out;
}

/**
 * Route a Concedii write the way the real API would, returning the row the
 * page re-renders from. Anything unrecognised falls through to a benign
 * success, exactly like the rest of Test Mode.
 */
export function mockLeaveWrite(
  pathname: string,
  method: string,
  body: Record<string, unknown>,
): unknown {
  const parts = pathname.split('/').filter(Boolean); // api, leave, …
  const tail = parts.slice(2);

  if (tail[0] === 'requests') {
    const id = tail[1];
    if (method === 'POST' && !id) {
      return { request: createLeaveRequestMock(body) };
    }
    if (id && method === 'PATCH') {
      return { request: patchLeaveRequestMock(id, body as Partial<LeaveRequest>) };
    }
    if (id && method === 'DELETE') {
      deleteLeaveRequestMock(id);
      return {};
    }
    if (id && method === 'POST') {
      const action = tail[2];
      const note = typeof body.note === 'string' ? body.note : null;
      if (action === 'approve') return { request: decideLeaveRequestMock(id, true, note) };
      if (action === 'reject') return { request: decideLeaveRequestMock(id, false, note) };
      if (action === 'cancel') {
        return { request: patchLeaveRequestMock(id, { status: 'CANCELLED' }) };
      }
    }
  }

  if (tail[0] === 'entitlement' && method === 'PUT') {
    const days = typeof body.days === 'number' ? body.days : null;
    return setLeaveEntitlementMock(tail[1] ?? '', days);
  }

  return { ok: true };
}
